/**
 * Spec Phase 4 Slice 4E — ExaminerViewDock.
 * Phase 7 Slice 7F — auto-open + flag flash on undefined-numeral
 * click.
 * Phase 8 Slice 8C — real flag rendering with hover affordances:
 *   - Resolve (drafter handled it)
 *   - Dismiss (intentional, not an error)
 *   - Acknowledge (seen, will revisit)
 *   - Jump (scroll editor / right rail to the source)
 * The Phase 7 numeral-click behaviour stays — undefined-numeral
 * clicks still open the dock and flash any matching flag.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Check, Eye, MoveRight, ShieldAlert, X } from 'lucide-react';
import { useSpecStore } from '@/store/specStore';
import { subscribe, type NumeralEvent } from '@/lib/numeralEventBus';
import Dock from './Dock';

const flagKindLabel: Record<string, string> = {
  claim_only: 'Claim only',
  spec_only: 'Spec only',
  numeral_undefined: 'Numeral undefined',
  figure_undescribed: 'Figure undescribed',
  antecedent_basis: 'Antecedent basis',
};

const railIcon =
  'inline-flex h-5 w-5 items-center justify-center rounded-sm text-zinc-300 ' +
  'opacity-0 group-hover:opacity-100 hover:text-zinc-700 hover:bg-zinc-100 ' +
  'transition-opacity disabled:cursor-not-allowed';

export default function ExaminerViewDock(): JSX.Element {
  const flags = useSpecStore((s) => s.examinerFlags);
  const setDockOpen = useSpecStore((s) => s.setDockOpen);
  const resolveFlag = useSpecStore((s) => s.resolveFlag);
  const dismissFlag = useSpecStore((s) => s.dismissFlag);
  const acknowledgeFlag = useSpecStore((s) => s.acknowledgeFlag);
  const setActiveSection = useSpecStore((s) => s.setActiveSection);
  const setActiveParagraph = useSpecStore((s) => s.setActiveParagraph);
  const sections = useSpecStore((s) => s.sections);
  const paragraphsBySection = useSpecStore((s) => s.paragraphsBySection);
  const showToast = useSpecStore((s) => s.showToast);
  const revealNonce = useSpecStore((s) => s.examinerRevealNonce);

  // Hide resolved + dismissed by default — they're audit history
  // not actionable items. The dock still surfaces 'acknowledged'
  // since the drafter explicitly said "I'll come back to this".
  const visibleFlags = flags.filter(
    (f) => f.status === 'open' || f.status === 'acknowledged',
  );
  const openCount = flags.filter((f) => f.status === 'open').length;
  const flagRefs = useRef<Map<string, HTMLLIElement>>(new Map());
  const [flashFlagId, setFlashFlagId] = useState<string | null>(null);

  // After an Examiner run, bring this dock into view: it sits sixth in
  // the right rail, usually below the fold.
  useEffect(() => {
    if (!revealNonce) return;
    window.requestAnimationFrame(() => {
      const el = document.querySelector('[data-testid="spec-dock-examiner-view"]');
      const rail = el?.closest('[data-testid="spec-dock-rail"]') as HTMLElement | null;
      if (!el || !rail) return;
      // Scroll the rail only. scrollIntoView also scrolls the page's
      // overflow-hidden body, shifting the whole workbench up.
      const top = el.getBoundingClientRect().top - rail.getBoundingClientRect().top;
      const target = rail.scrollTop + top;
      if (typeof rail.scrollTo === 'function') rail.scrollTo({ top: target, behavior: 'smooth' });
      else rail.scrollTop = target;
    });
  }, [revealNonce]);

  useEffect(() => {
    const unsub = subscribe((ev: NumeralEvent) => {
      if (ev.kind !== 'numeral:click') return;
      if (!ev.undefined_) return;
      setDockOpen('examinerView', true);
      // Match by flag_kind + term. Defensive: undefined-numeral
      // chips emit the raw numeral; the examiner flag's term
      // column is also the raw numeral for numeral_undefined kind.
      const match = flags.find(
        (f) => f.flag_kind === 'numeral_undefined' && f.term === ev.numeral,
      );
      if (!match) return;
      window.requestAnimationFrame(() => {
        const el = flagRefs.current.get(match.id);
        if (el) {
          el.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest',
          });
        }
        setFlashFlagId(match.id);
        window.setTimeout(() => {
          setFlashFlagId((cur) => (cur === match.id ? null : cur));
        }, 1200);
      });
    });
    return () => {
      unsub();
    };
  }, [flags, setDockOpen]);

  return (
    <Dock
      dockKey="examinerView"
      icon={<ShieldAlert className="h-3.5 w-3.5 text-zinc-500" aria-hidden />}
      title="Examiner view"
      pillCount={openCount}
      meta={
        flags.length === 0
          // BUG-014b — replace dev-time "Phase 8 button" label
          // (build-phase shorthand that leaked into the user-
          // facing copy) with the same wording the inner empty
          // state at line 101-103 already uses.
          ? 'No flags. Click Examiner in the action bar to run a matter-wide review.'
          : undefined
      }
      testId="spec-dock-examiner-view"
    >
      {visibleFlags.length === 0 ? (
        <p className="text-[11px] text-zinc-500">
          No active flags. Click <strong>Examiner</strong> in the
          action bar to run a matter-wide review.
        </p>
      ) : (
        <ul className="space-y-1">
          {visibleFlags.map((f) => {
            let detail: Record<string, unknown> = {};
            try {
              detail = JSON.parse(f.detail_json || '{}');
            } catch { /* malformed json — render bare */ }
            const targetParaId = (detail.originating_paragraph_id as string)
              || null;
            const targetClaimId = (detail.originating_claim_id as string)
              || null;
            const targetClaimNumber = typeof detail.originating_claim_number === 'number'
              ? detail.originating_claim_number as number : null;
            const suggestion = (detail.suggestion as string) || '';
            const issueText = (detail.issue as string) || '';
            return (
            <li
              key={f.id}
              ref={(el) => {
                if (el) flagRefs.current.set(f.id, el);
                else flagRefs.current.delete(f.id);
              }}
              className={[
                'group rounded-sm border border-zinc-200 bg-white px-2 py-1.5',
                flashFlagId === f.id ? 's6-row-flash' : '',
              ].join(' ')}
              data-testid={`spec-examiner-flag-${f.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <span className="font-mono text-[10px] text-zinc-600">
                  {flagKindLabel[f.flag_kind] ?? f.flag_kind}
                </span>
                <span
                  className={[
                    'rounded-sm px-1 font-mono text-[9px]',
                    f.status === 'open'
                      ? 'bg-amber-50 text-amber-700'
                      : f.status === 'acknowledged'
                        ? 'bg-blue-50 text-blue-700'
                        : f.status === 'resolved'
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-zinc-100 text-zinc-500',
                  ].join(' ')}
                >
                  {f.status}
                </span>
              </div>
              <p className="mt-0.5 text-[11px] text-zinc-700 line-clamp-2">
                <strong className="font-mono">{f.term}</strong>
                {suggestion ? <> · <span className="text-zinc-600">{suggestion}</span></> : null}
                {issueText ? <> · <span className="text-zinc-600">{issueText}</span></> : null}
              </p>
              <div className="mt-1 flex items-center justify-end gap-1">
                <button
                  type="button"
                  className={railIcon}
                  onClick={() => {
                    void resolveFlag(f.id);
                    showToast(`Flag resolved`);
                  }}
                  title="Mark resolved (drafter handled it)"
                  data-testid={`spec-flag-resolve-${f.id}`}
                  disabled={f.status === 'resolved'}
                >
                  <Check className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  className={railIcon}
                  onClick={() => {
                    void dismissFlag(f.id);
                    showToast(`Flag dismissed`);
                  }}
                  title="Dismiss (intentional, not an error)"
                  data-testid={`spec-flag-dismiss-${f.id}`}
                  disabled={f.status === 'dismissed'}
                >
                  <X className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  className={railIcon}
                  onClick={() => void acknowledgeFlag(f.id)}
                  title="Acknowledge (seen, will revisit)"
                  data-testid={`spec-flag-ack-${f.id}`}
                  disabled={f.status === 'acknowledged'}
                >
                  <Eye className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  className={railIcon}
                  disabled={!targetParaId && !targetClaimId}
                  onClick={() => {
                    if (targetParaId) {
                      // Find the section that owns this paragraph,
                      // activate it, then activate the paragraph.
                      for (const sec of sections) {
                        const list = paragraphsBySection[sec.id] ?? [];
                        if (list.some((p) => p.id === targetParaId)) {
                          setActiveSection(sec.id);
                          setActiveParagraph(targetParaId);
                          // Defer scroll to next frame so the section
                          // has had time to render.
                          window.requestAnimationFrame(() => {
                            const el = document.querySelector(
                              `[data-testid$="-${targetParaId}"]`,
                            );
                            if (el) {
                              (el as HTMLElement).scrollIntoView({
                                behavior: 'smooth', block: 'center',
                              });
                            }
                          });
                          return;
                        }
                      }
                      showToast('Paragraph not found.');
                    } else if (targetClaimId) {
                      // Open Frozen Claims dock so the claim card
                      // becomes visible. Phase 9 wires per-claim
                      // scroll-into-view.
                      useSpecStore.getState().setDockOpen('frozenClaims', true);
                      showToast(targetClaimNumber
                        ? `Claim ${targetClaimNumber} is in Frozen claims at the top of this rail.`
                        : 'The claim is in Frozen claims at the top of this rail.');
                    }
                  }}
                  title={
                    targetParaId
                      ? 'Jump to paragraph'
                      : targetClaimId
                        ? 'Jump to claim'
                        : 'No locator'
                  }
                  data-testid={`spec-flag-jump-${f.id}`}
                >
                  <MoveRight className="h-3 w-3" />
                </button>
              </div>
            </li>
            );
          })}
        </ul>
      )}
    </Dock>
  );
}
